// src/api.js
// Phase 1 — Intercepteur refresh token automatique
// Si une requête reçoit 401 → tente POST /auth/refresh → rejoue la requête
// Si le refresh échoue → redirige vers /login?expired=1
import axios from "axios";

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:8001",
  headers: { "Content-Type": "application/json" },
});

// ── Injecte le token JWT dans chaque requête ──────────────────────
API.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Flag anti-boucle : on ne tente le refresh qu'une seule fois ───
let _isRefreshing = false;
let _waitQueue    = []; // requêtes en attente pendant le refresh

function processQueue(error, token = null) {
  _waitQueue.forEach(({ resolve, reject }) =>
    error ? reject(error) : resolve(token)
  );
  _waitQueue = [];
}

// ── Intercepteur 401 avec refresh automatique ─────────────────────
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Cas 1 : 401 sur /auth/refresh lui-même → déconnexion immédiate
    if (
      error.response?.status === 401 &&
      originalRequest.url?.includes("/auth/refresh")
    ) {
      _forceLogout();
      return Promise.reject(error);
    }

    // Cas 2 : 401 sur autre requête, pas encore réessayée
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (_isRefreshing) {
        // D'autres requêtes attendent déjà le refresh — on les met en queue
        return new Promise((resolve, reject) => {
          _waitQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return API(originalRequest);
        });
      }

      _isRefreshing = true;
      const refreshToken = localStorage.getItem("refresh_token");

      if (!refreshToken) {
        _forceLogout();
        return Promise.reject(error);
      }

      try {
        const res = await axios.post(
          `${API.defaults.baseURL}/auth/refresh`,
          { refresh_token: refreshToken },
          { headers: { "Content-Type": "application/json" } }
        );

        const { access_token, refresh_token: newRefresh, is_admin } = res.data;
        localStorage.setItem("access_token",  access_token);
        localStorage.setItem("refresh_token", newRefresh);
        localStorage.setItem("is_admin",      String(Boolean(is_admin)));

        API.defaults.headers.common.Authorization = `Bearer ${access_token}`;
        processQueue(null, access_token);

        // Rejouer la requête originale
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return API(originalRequest);

      } catch (refreshError) {
        processQueue(refreshError, null);
        _forceLogout();
        return Promise.reject(refreshError);
      } finally {
        _isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

function _forceLogout() {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("token_type");
  localStorage.removeItem("is_admin");
  localStorage.removeItem("user_info");
  window.dispatchEvent(new Event("auth-changed"));
  if (window.location.pathname !== "/login") {
    window.location.href = "/login?expired=1";
  }
}

export default API;
